import { useState } from "react";
import { ChevronDown, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { useVeri } from "@/lib/veri";
import { useOturum } from "@/lib/oturum";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";

interface Kimlikler {
  asil: { id: string; ad: string; rol: string; rolEtiket: string };
  kullanicilar: { id: string; ad: string; rol: string; rolEtiket: string; telefon: string | null; aktifMi: boolean }[];
}

/**
 * Kimlik değiştirme (demo/destek): yönetici, ekipteki rollerden biri gibi bakar.
 * Sunucu tarafı gerçek oturum açar — yetki duvarları taklit edilmez, gerçekten uygulanır.
 */
export function KimlikSecici() {
  const { ben, yenile } = useOturum();
  const [acik, setAcik] = useState(false);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const { veri } = useVeri<Kimlikler>(ben?.yonetici ? "/auth/kimlikler" : null);
  if (!ben?.yonetici || !veri) return null;

  const gec = async (id: string) => {
    setMesgul(id);
    try {
      await api.post(id === veri.asil.id ? "/auth/kimlik-birak" : `/auth/kimlik/${id}`);
      await yenile();
      setAcik(false);
    } finally {
      setMesgul(null);
    }
  };

  const taklit = Boolean(ben.asil);
  return (
    <div className="relative">
      <button
        onClick={() => setAcik((a) => !a)}
        aria-expanded={acik}
        className={cn(
          "flex h-kontrol-kucuk items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition",
          taklit ? "border-orange bg-orange-soft text-orange" : "border-line bg-surface text-ink-2 hover:border-line-strong",
        )}
        title={cevir("Change ID (demo)")}
      >
        <UserCog size={14} />
        <span className="hidden sm:inline">{taklit ? `${ben.ad.split(" ")[0]} olarak` : cevir("Identity")}</span>
        <ChevronDown size={13} />
      </button>
      {acik && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setAcik(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-72 rounded-md border border-line bg-surface p-1 shadow-[var(--shadow-lg)]">
            <div className="px-2 py-2 text-2xs uppercase tracking-wider text-ink-3">{cevir("Demo IDs — screen seen by role")}</div>
            {veri.kullanicilar.map((k) => {
              const secili = k.id === ben.id;
              return (
                <button
                  key={k.id}
                  disabled={Boolean(mesgul)}
                  onClick={() => void gec(k.id)}
                  className={cn("flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-surface-2 disabled:opacity-50", secili && "bg-surface-2")}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy text-2xs font-bold text-white dark:text-[#0a141c]">
                    {k.ad.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{k.ad}</span>
                    <span className="block truncate text-xs text-ink-3">{cevir(k.rolEtiket)}</span>
                  </span>
                  {mesgul === k.id ? <span className="text-xs text-ink-3">{cevir("switching...")}</span> : secili && <span className="text-xs font-semibold text-orange">{cevir("open")}</span>}
                </button>
              );
            })}
            {taklit && (
              <button
                disabled={Boolean(mesgul)}
                onClick={() => void gec(veri.asil.id)}
                className="mt-1 flex w-full items-center gap-2 rounded border-t border-line px-2 py-2 text-left text-sm font-medium hover:bg-surface-2"
              >
                ← {veri.asil.ad} {cevir("continue as")}
              </button>
            )}
            <p className="px-2 py-2 text-2xs leading-snug text-ink-3">{cevir("Switches fall into audit log; permissions are real role's permissions.")}</p>
          </div>
        </>
      )}
    </div>
  );
}
